import { RootState } from "core-integration/src/store";
import {
  getEntry,
  getPrivateClassicCupLeaguesForEntry,
  getPrivateClassicLeaguesForEntry,
  getPrivateH2HLeaguesForEntry,
  getPublicClassicCupLeaguesForEntry,
  getPublicClassicLeaguesForEntry,
  getPublicH2HLeaguesForEntry,
  getSystemClassicCupLeaguesForEntry,
  getSystemClassicLeaguesForEntry,
} from "core-integration/src/store/entries/reducers";
import { ILeagueEntry } from "core-integration/src/store/entries/types";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link as RouterLink } from "react-router";
import Button from "../../../Button";
import Movement from "../../../leagues/Movement";
import MyLeagueCupSummary from "../../../leagues/MyLeagueCupSummary";
import { LeagueSummaryTable } from "../../../leagues/Styles";
import {
  getBroadcasterLeagues,
  getFilteredSystemClassicLeagues,
} from "../../../leagues/utils";
import Panel from "../../../Panel";
import TabHeading from "../../../TabHeading";
import EntryTabs from "../../../tabs/EntryTabs";
import TabPanel from "../../../tabs/TabPanel";
import { VisuallyHidden } from "../../../Utils";
import {
  Container,
  MovementCell,
  MovementHead,
  Name,
  NameHead,
  NoCupsWrap,
  Rank,
  RankHead,
  StyledLink,
} from "./styles";
import {
  ILeagueSummaryProps,
  IManageYourLeaguesProps,
  IRowProps,
} from "./types";

const LeagueSummary = ({ leagues, title }: ILeagueSummaryProps) => {
  const { t } = useTranslation();
  return (
    <div>
      <div>
        <TabHeading title={title} />
      </div>
      <LeagueSummaryTable>
        <thead>
          <tr>
            <MovementHead scope="col">
              <VisuallyHidden>
                {t("myTeam.leagueSummary.movement", "Movement")}
              </VisuallyHidden>
            </MovementHead>
            <RankHead scope="col">
              {t("myTeam.leagueSummary.rank", "Rank")}
            </RankHead>
            <NameHead scope="col">
              {t("myTeam.leagueSummary.league", "League")}
            </NameHead>
          </tr>
        </thead>
        <tbody>
          {leagues.map((l) => (
            <LeagueSummaryRow key={l.id} leagueEntry={l} />
          ))}
        </tbody>
      </LeagueSummaryTable>
    </div>
  );
};

const LeagueSummaryRow = ({ leagueEntry }: IRowProps) => {
  const linkToLeague = (l: ILeagueEntry) =>
    `/leagues/${l.id}/standings/${l.scoring}`;
  return (
    <tr>
      <MovementCell>
        <Movement
          lastRank={leagueEntry.entry_last_rank}
          rank={leagueEntry.entry_rank}
        />
      </MovementCell>
      <Rank>
        {leagueEntry.entry_rank
          ? leagueEntry.entry_rank.toLocaleString()
          : null}
      </Rank>
      <Name>
        <StyledLink to={linkToLeague(leagueEntry)}>
          {leagueEntry.name}
        </StyledLink>
      </Name>
    </tr>
  );
};

const ManageYourLeagues = ({ entryId }: IManageYourLeaguesProps) => {
  const { t } = useTranslation();
  const entry = useSelector((state: RootState) => getEntry(state, entryId));
  const privateClassicLeagues = useSelector((state: RootState) =>
    getPrivateClassicLeaguesForEntry(state, entryId),
  );
  const privateH2HLeagues = useSelector((state: RootState) =>
    getPrivateH2HLeaguesForEntry(state, entryId),
  );
  const publicClassicLeagues = useSelector((state: RootState) =>
    getPublicClassicLeaguesForEntry(state, entryId),
  );
  const publicH2HLeagues = useSelector((state: RootState) =>
    getPublicH2HLeaguesForEntry(state, entryId),
  );
  const systemClassicLeagues = useSelector((state: RootState) =>
    getSystemClassicLeaguesForEntry(state, entryId),
  );

  const privateClassicCupLeagues = useSelector((state: RootState) =>
    getPrivateClassicCupLeaguesForEntry(state, entryId),
  );
  const publicClassicCupLeagues = useSelector((state: RootState) =>
    getPublicClassicCupLeaguesForEntry(state, entryId),
  );
  const systemClassicCupLeagues = useSelector((state: RootState) =>
    getSystemClassicCupLeaguesForEntry(state, entryId),
  );

  if (!entry) {
    return null;
  }

  // Split broadcaster leagues out from system classic leagues
  const broadcasterLeagues = getBroadcasterLeagues(systemClassicLeagues);
  const filteredSystemClassicLeagues = getFilteredSystemClassicLeagues(
    systemClassicLeagues,
    broadcasterLeagues,
  );

  const hasCupLeagues =
    privateClassicCupLeagues.length > 0 ||
    publicClassicCupLeagues.length > 0 ||
    systemClassicCupLeagues.length > 0;

  // Split broadcaster cup leagues out from system classic cup leagues
  const broadcasterCupLeagues = getBroadcasterLeagues(systemClassicCupLeagues);
  const filteredSystemCupLeagues = getFilteredSystemClassicLeagues(
    systemClassicCupLeagues,
    broadcasterCupLeagues,
  );

  return (
    <Container>
      <Panel>
        <StyledLink to="/leagues">
          <Panel.Header
            title={t("myTeam.leaguesCupsN", "Manage Your Leagues")}
          />
        </StyledLink>
        <Panel.Body>
          <EntryTabs>
            <TabPanel label={t("myTeam.leaguesTab", "Leagues")} link="leagues">
              {broadcasterLeagues.length > 0 && (
                <LeagueSummary
                  title={t("myTeam.broadcaster", "Broadcaster League")}
                  leagues={broadcasterLeagues}
                />
              )}
              {privateClassicLeagues.length > 0 && (
                <LeagueSummary
                  title={t("myTeam.privateClassic", "Private Classic Leagues")}
                  leagues={privateClassicLeagues}
                />
              )}
              {privateH2HLeagues.length > 0 && (
                <LeagueSummary
                  title={t("myTeam.privateH2H", "Private Head-to-Head Leagues")}
                  leagues={privateH2HLeagues}
                />
              )}
              {publicClassicLeagues.length > 0 && (
                <LeagueSummary
                  title={t("myTeam.publicClassic", "Public Classic Leagues")}
                  leagues={publicClassicLeagues}
                />
              )}
              {publicH2HLeagues.length > 0 && (
                <LeagueSummary
                  title={t("myTeam.publicH2H", "Public Head-to-Head Leagues")}
                  leagues={publicH2HLeagues}
                />
              )}
              <LeagueSummary
                title={t("myTeam.general", "General Leagues")}
                leagues={filteredSystemClassicLeagues}
              />
            </TabPanel>
            <TabPanel label={t("leaguesNav.cups", "Cups")} link="cups">
              {hasCupLeagues ? (
                <>
                  {privateClassicCupLeagues.length > 0 && (
                    <MyLeagueCupSummary
                      entryId={entry.id}
                      leagues={privateClassicCupLeagues}
                      title={t("myTeam.leagueCups", "League cups")}
                    />
                  )}
                  {publicClassicCupLeagues.length > 0 && (
                    <MyLeagueCupSummary
                      entryId={entry.id}
                      leagues={publicClassicCupLeagues}
                      title={t("myTeam.publicCups", "Public cups")}
                    />
                  )}
                  {filteredSystemCupLeagues.length > 0 && (
                    <MyLeagueCupSummary
                      entryId={entry.id}
                      leagues={filteredSystemCupLeagues}
                      title={t("myTeam.generalCups", "General cups")}
                    />
                  )}
                  {broadcasterCupLeagues.length > 0 && (
                    <MyLeagueCupSummary
                      entryId={entry.id}
                      leagues={broadcasterCupLeagues}
                      title={t("myTeam.broadcasterCups", "Broadcaster cups")}
                    />
                  )}
                </>
              ) : (
                <NoCupsWrap>
                  <p>
                    {t("myTeam.none", "None of your leagues have a cup yet.")}
                  </p>
                </NoCupsWrap>
              )}
            </TabPanel>
          </EntryTabs>
        </Panel.Body>
      </Panel>

      <Button width={1} to="/leagues" as={RouterLink}>
        {t("myTeam.createAndJoinLeagues", "Create and join leagues")}
      </Button>
    </Container>
  );
};

export default ManageYourLeagues;
